import React, { useEffect } from "react";
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { ListItemIcon } from "@outpave/ds";
import { CheckmarkCircle02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Stack } from "components/layout/reusableLayouts";
import StatusPage from "components/common/StatusPage";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutlined";
import { useLazyQuery, useQuery } from "@apollo/client";
import {
  GET_COMPANY,
  CHECK_ACTIVATION_COMPANY_STATUS,
} from "../graphql/company";
import { Status } from "../enums/Company";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ApplicationStatus from "enums/ApplicationStatus";
import { toast } from "react-toastify";
import { useBrandingContext } from "context/BrandingContext";

const Pending: React.FC = () => {
  const [appStatus, setAppStatus] = React.useState(ApplicationStatus.PENDING);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { platform } = useBrandingContext();
  const platformName = platform?.name || "";

  const {
    data: companyData,
    loading: loadingCompany,
    startPolling,
    stopPolling,
  } = useQuery(GET_COMPANY, {
    fetchPolicy: "network-only",
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const companyStatus = companyData?.company?.status;
  const isSetupInProgress = companyStatus === Status.Pending;

  const [checkActivationStatus, { data: applicationData }] = useLazyQuery(
    CHECK_ACTIVATION_COMPANY_STATUS,
    {
      fetchPolicy: "network-only",
      pollInterval: 10000,
      onError: (error) => {
        toast.error(error.message);
      },
    },
  );

  useEffect(() => {
    if (!companyData) return;
    if (isSetupInProgress) return;
    if (appStatus !== ApplicationStatus.PENDING) return;
    const applicationStatus = applicationData?.checkActivationStatus?.status;
    if (
      applicationStatus === ApplicationStatus.DENIED ||
      applicationStatus === ApplicationStatus.CLOSED
    ) {
      setAppStatus(ApplicationStatus.DENIED);
      toast.error(t("Application Could Not Be Processed"));
      navigate("/inactive");
    } else if (applicationStatus === ApplicationStatus.APPROVED) {
      toast.success(t("Application Approved"));
      toast.success(t("Creating Account"));
      setAppStatus(ApplicationStatus.APPROVED);
    } else if (applicationStatus === ApplicationStatus.IN_REVIEW) {
      navigate("/upload");
    }

    checkActivationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationData, checkActivationStatus, companyData, isSetupInProgress]);

  useEffect(() => {
    if (appStatus !== ApplicationStatus.APPROVED) return;
    startPolling(10000);
    if (companyStatus === Status.Active) {
      stopPolling();
      navigate("/admin/dashboard");
    } else if (companyStatus === Status.Inactive) {
      stopPolling();
      navigate("/inactive");
    }
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStatus, companyStatus]);

  if (loadingCompany && !companyData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (appStatus === ApplicationStatus.APPROVED) {
    return (
      <StatusPage
        title={t("ApprovedTitle", { platformName })}
        message={t("ApprovedDescription")}
        icon={CheckmarkCircle02Icon}
        tone="success"
        action={{
          label: t("Get started"),
          onClick: () => navigate("/admin/dashboard"),
        }}
      />
    );
  }

  const whatHappensNext = [
    {
      icon: <GppGoodOutlinedIcon />,
      title: t("IdentityVerificationTitle"),
      description: t("IdentityVerificationDescription"),
    },
    {
      icon: <AccessTimeOutlinedIcon />,
      title: t("ComplianceReviewTitle"),
      description: t("ComplianceReviewDescription"),
    },
    {
      icon: <MailOutlineIcon />,
      title: t("DecisionActivationTitle"),
      description: t("DecisionActivationDescription"),
    },
  ];

  return (
    <StatusPage
      title={t("Application submitted")}
      message={t("ApplicationSubmittedDescription")}
      icon={CheckmarkCircle02Icon}
    >
      <Stack gap="var(--space-3)" sx={{ width: "100%" }}>
        <Typography variant="overline">{t("What happens next")}</Typography>
        <List disablePadding>
          {whatHappensNext.map((item) => (
            <ListItem key={item.title} disableGutters alignItems="flex-start">
              <ListItemIcon wrapper="brand">{item.icon}</ListItemIcon>
              <ListItemText primary={item.title} secondary={item.description} />
            </ListItem>
          ))}
        </List>
      </Stack>
    </StatusPage>
  );
};

export default Pending;
